import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Download, Search, X, ZoomIn } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { scrollToId } from "../lib";
import { SEGMENTS } from "../data/products";

const TOTAL_PRODUCTS = SEGMENTS.reduce((n, s) => n + s.products.length, 0);

const enquireProduct = (segmentName, productName) => {
    window.dispatchEvent(
        new CustomEvent("ss:enquire-product", {
            detail: { interest: segmentName, product: productName },
        }),
    );
    scrollToId("contact");
};

export default function Products() {
    const [segId, setSegId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState("");
    const [zoomed, setZoomed] = useState(false);
    const segment = SEGMENTS.find((s) => s.id === segId) || null;

    const allProducts = useMemo(
        () =>
            SEGMENTS.flatMap((s) =>
                s.products.map((p) => ({ ...p, segment: s })),
            ),
        [],
    );

    const q = query.trim().toLowerCase();
    const results = q
        ? allProducts.filter(
              (p) =>
                  p.name.toLowerCase().includes(q) ||
                  p.specs.some((sp) => sp.toLowerCase().includes(q)),
          )
        : null;

    const items =
        results ??
        (segment ? segment.products.map((p) => ({ ...p, segment })) : []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setZoomed(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const pickSegment = (id) => {
        setSegId(id);
        setSelected(null);
        setQuery("");
    };

    const onSearch = (e) => {
        setQuery(e.target.value);
        if (e.target.value.trim()) setSelected(null);
    };

    const clearSearch = () => {
        setQuery("");
        setSegId(null);
        setSelected(null);
    };

    const viewKey = selected
        ? `detail-${selected.slug}`
        : results
          ? "search"
          : segment
            ? `segment-${segment.id}`
            : "overview";

    return (
        <section
            id="products"
            data-testid="products-section"
            className="relative overflow-hidden py-24 lg:py-32"
        >
            <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-accent/5 blur-[140px]" />
            <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHead
                        label="What We Manufacture"
                        title="Everything the theatre needs."
                    />
                    <Reveal delay={0.15}>
                        <div className="flex max-w-sm flex-col items-start gap-5">
                            <p className="text-sm leading-relaxed text-slate-400">
                                Manufactured in our own production units — and
                                sourced globally where the problem demands it.
                            </p>
                            <button
                                data-testid="catalogue-open-button"
                                onClick={() =>
                                    window.dispatchEvent(
                                        new Event("ss:catalogue"),
                                    )
                                }
                                className="inline-flex items-center gap-2 rounded-full border border-line px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-accent hover:text-accent"
                            >
                                <Download className="h-4 w-4" />
                                Get the Catalogue
                            </button>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.08}>
                    <div className="relative mt-10 max-w-md">
                        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid="product-search-input"
                            value={query}
                            onChange={onSearch}
                            placeholder={`Search ${TOTAL_PRODUCTS} products — e.g. fowler bed, sterilizer, LED…`}
                            className="w-full rounded-full border border-line bg-panel/70 py-3.5 pl-11 pr-11 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-accent/60 focus:ring-1 focus:ring-accent/40"
                        />
                        {query && (
                            <button
                                data-testid="product-search-clear"
                                onClick={clearSearch}
                                aria-label="Clear search"
                                className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-white/5 hover:text-white"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </Reveal>

                {segment && !selected && !results && (
                    <div
                        data-testid="segment-tabs"
                        className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-2"
                    >
                        {SEGMENTS.map((s) => (
                            <button
                                key={s.id}
                                data-testid={`segment-tab-${s.id}`}
                                onClick={() => pickSegment(s.id)}
                                className={`shrink-0 rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                                    s.id === segId
                                        ? "border-accent bg-accent text-ink"
                                        : "border-line text-slate-400 hover:border-slate-500 hover:text-white"
                                }`}
                            >
                                {s.tab}
                            </button>
                        ))}
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {selected ? (
                        <motion.div
                            key={viewKey}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4, ease: EASE }}
                            className="mt-10"
                            data-testid="product-detail-view"
                        >
                            <button
                                data-testid="back-to-products-button"
                                onClick={() => setSelected(null)}
                                className="mb-8 inline-flex items-center gap-2 border-b border-transparent pb-0.5 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:border-accent hover:text-accent"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to{" "}
                                {results ? "results" : selected.segment.tab}
                            </button>

                            <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
                                <button
                                    data-testid="product-image-zoom-button"
                                    onClick={() => setZoomed(true)}
                                    aria-label="Enlarge product photo"
                                    className="group/img product-tile relative flex cursor-zoom-in items-center justify-center rounded-2xl border border-line p-6 transition-colors duration-300 hover:border-accent/40 sm:p-10"
                                >
                                    <img
                                        src={selected.img}
                                        alt={selected.name}
                                        data-testid="product-detail-image"
                                        className="max-h-[300px] w-full object-contain lg:max-h-[440px]"
                                    />
                                    <span className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-ink/80 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover/img:opacity-100">
                                        <ZoomIn className="h-3 w-3" />
                                        Enlarge
                                    </span>
                                </button>
                                <div>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-accent">
                                        {selected.segment.name}
                                    </span>
                                    <h3
                                        data-testid="product-detail-name"
                                        className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl"
                                    >
                                        {selected.name}
                                    </h3>
                                    <div className="mt-6 border-t border-line pt-6">
                                        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                                            Key Specifications
                                        </p>
                                        <ul
                                            data-testid="product-detail-specs"
                                            className="max-h-[300px] space-y-3 overflow-y-auto pr-2"
                                        >
                                            {selected.specs.map((s, i) => (
                                                <li
                                                    key={i}
                                                    className="flex items-start gap-3 text-sm leading-relaxed text-slate-300"
                                                >
                                                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rotate-45 bg-accent" />
                                                    {s}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <button
                                        data-testid="product-enquire-button"
                                        onClick={() =>
                                            enquireProduct(
                                                selected.segment.name,
                                                selected.name,
                                            )
                                        }
                                        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(0,240,255,0.3)]"
                                    >
                                        Enquire about this product
                                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ) : results ? (
                        <motion.div
                            key={viewKey}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            className="mt-10"
                            data-testid="product-grid-view"
                        >
                            <div className="mb-8">
                                <h3
                                    data-testid="product-search-count"
                                    className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl"
                                >
                                    {items.length}{" "}
                                    {items.length === 1 ? "result" : "results"}{" "}
                                    for “{query.trim()}”
                                </h3>
                                <p className="mt-2 text-sm text-slate-400">
                                    Across all product segments
                                </p>
                            </div>

                            {items.length === 0 ? (
                                <div
                                    data-testid="product-search-empty"
                                    className="rounded-2xl border border-line bg-panel/50 px-6 py-16 text-center"
                                >
                                    <p className="font-display text-lg font-bold text-white">
                                        No products match “{query.trim()}”.
                                    </p>
                                    <p className="mt-2 text-sm text-slate-400">
                                        Try a different term — or browse the
                                        segments.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-5">
                                    {items.map((p, i) => (
                                        <motion.button
                                            key={p.slug}
                                            initial={{ opacity: 0, y: 16 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                                duration: 0.4,
                                                delay: Math.min(i * 0.04, 0.3),
                                                ease: EASE,
                                            }}
                                            data-testid={`product-card-${p.slug}`}
                                            onClick={() => setSelected(p)}
                                            className="group overflow-hidden rounded-2xl border border-line bg-panel text-left transition-colors duration-300 hover:border-accent/50"
                                        >
                                            <div className="product-tile aspect-[4/3] p-3">
                                                <img
                                                    src={p.img}
                                                    alt={p.name}
                                                    loading="lazy"
                                                    className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between gap-2 p-4">
                                                <div>
                                                    <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-accent">
                                                        {p.segment.tab}
                                                    </span>
                                                    <span className="text-xs font-semibold leading-snug text-white sm:text-sm">
                                                        {p.name}
                                                    </span>
                                                </div>
                                                <ArrowUpRight className="h-4 w-4 shrink-0 self-center text-slate-500 transition-colors duration-300 group-hover:text-accent" />
                                            </div>
                                        </motion.button>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    ) : segment ? (
                        <motion.div
                            key={viewKey}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            className="mt-10"
                            data-testid="product-grid-view"
                        >
                            <button
                                data-testid="back-to-segments-button"
                                onClick={() => setSegId(null)}
                                className="mb-8 inline-flex items-center gap-2 border-b border-transparent pb-0.5 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:border-accent hover:text-accent"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                All segments
                            </button>

                            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
                                <div>
                                    <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                                        {segment.name}
                                    </h3>
                                    <p className="mt-2 text-sm text-slate-400">
                                        {segment.tagline}
                                    </p>
                                </div>
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
                                    {segment.products.length} products
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-5">
                                {items.map((p, i) => (
                                    <motion.button
                                        key={p.slug}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.4,
                                            delay: Math.min(i * 0.05, 0.35),
                                            ease: EASE,
                                        }}
                                        data-testid={`product-card-${p.slug}`}
                                        onClick={() => setSelected(p)}
                                        className="group overflow-hidden rounded-2xl border border-line bg-panel text-left transition-colors duration-300 hover:border-accent/50"
                                    >
                                        <div className="product-tile aspect-[4/3] p-3">
                                            <img
                                                src={p.img}
                                                alt={p.name}
                                                loading="lazy"
                                                className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between gap-2 p-4">
                                            <span className="text-xs font-semibold leading-snug text-white sm:text-sm">
                                                {p.name}
                                            </span>
                                            <ArrowUpRight className="h-4 w-4 shrink-0 self-center text-slate-500 transition-colors duration-300 group-hover:text-accent" />
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key={viewKey}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            className="mt-10"
                            data-testid="segment-overview"
                        >
                            <p className="mb-8 max-w-md text-sm leading-relaxed text-slate-400">
                                Choose a segment to explore its complete range —
                                or search above to jump straight to a product.
                            </p>
                            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                {SEGMENTS.map((s, i) => (
                                    <motion.button
                                        key={s.id}
                                        initial={{ opacity: 0, y: 18 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.45,
                                            delay: Math.min(i * 0.05, 0.4),
                                            ease: EASE,
                                        }}
                                        data-testid={`segment-card-${s.id}`}
                                        onClick={() => pickSegment(s.id)}
                                        className={`group overflow-hidden rounded-2xl border border-line bg-panel text-left transition-colors duration-300 hover:border-accent/50 ${
                                            i === 0 ? "sm:col-span-2" : ""
                                        }`}
                                    >
                                        <div
                                            className={`product-tile flex items-center justify-center p-6 ${
                                                i === 0
                                                    ? "aspect-[16/8]"
                                                    : "aspect-[16/9]"
                                            }`}
                                        >
                                            <img
                                                src={s.products[0].img}
                                                alt={s.name}
                                                loading="lazy"
                                                className="max-h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="flex items-end justify-between gap-3 p-5">
                                            <div>
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
                                                    {String(i + 1).padStart(
                                                        2,
                                                        "0",
                                                    )}{" "}
                                                    · {s.products.length}{" "}
                                                    products
                                                </span>
                                                <h4 className="mt-2 font-display text-lg font-bold leading-snug text-white sm:text-xl">
                                                    {s.name}
                                                </h4>
                                                <p className="mt-1 text-xs leading-relaxed text-slate-400">
                                                    {s.tagline}
                                                </p>
                                            </div>
                                            <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:text-accent" />
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <AnimatePresence>
                {zoomed && selected && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setZoomed(false)}
                        data-testid="product-image-lightbox"
                        className="fixed inset-0 z-[95] flex cursor-zoom-out items-center justify-center bg-ink/95 p-4 backdrop-blur-md"
                    >
                        <motion.img
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.94, opacity: 0 }}
                            transition={{ duration: 0.3, ease: EASE }}
                            src={selected.img}
                            alt={selected.name}
                            className="product-tile max-h-[88vh] max-w-[92vw] rounded-xl object-contain p-4 sm:p-6"
                        />
                        <button
                            data-testid="lightbox-close-button"
                            onClick={() => setZoomed(false)}
                            aria-label="Close enlarged photo"
                            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-panel text-white transition-colors hover:border-accent/50 hover:text-accent"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
